import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { ScreenShare, Save, Plus, X, Pencil } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTenantScope } from '../context/TenantScopeContext';

// Tool di accesso remoto (2026-09-24, vedi ingestion/remoteAccess.ts): default del tenant,
// asset "visti e senza policy" da smaltire, policy in blocco per gruppo, catalogo dei tool
// (modificabile dal platform admin: vale per tutti i tenant).
interface Tool { key: string; name: string; process_names: string[]; software_patterns: string[]; publishers: string[]; domains: string[]; enabled: boolean; builtin: boolean }
interface Pending { id: number; hostname: string; ip_address: string | null; remote_access_last_tool: string; remote_access_last_signal: string; remote_access_last_seen_at: string; tool_name: string | null }
interface Group { id: number; name: string }
type TenantPolicy = 'info' | 'deny' | 'allow';

export default function RemoteAccessPage() {
  const { user, isAdmin } = useAuth();
  const { selectedTenantId, selectedTenantName } = useTenantScope();
  const [tools, setTools] = useState<Tool[]>([]);
  const [tenant, setTenant] = useState<{ policy: TenantPolicy; tools: string[] } | null>(null);
  const [pending, setPending] = useState<Pending[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [bulk, setBulk] = useState<{ groupId: string; policy: 'deny' | 'allow'; tools: string[] }>({ groupId: '', policy: 'allow', tools: [] });
  const [editing, setEditing] = useState<Tool | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const isPlatform = user?.scopeLevel === 'platform';

  async function loadTools() { setTools((await axios.get('/api/remote-access/tools')).data.data); }
  async function loadTenant() {
    if (!selectedTenantId) { setTenant(null); setPending([]); setGroups([]); return; }
    const [t, p, g] = await Promise.all([
      axios.get(`/api/remote-access/tenant/${selectedTenantId}`),
      axios.get('/api/remote-access/pending', { params: { tenantId: selectedTenantId } }),
      axios.get('/api/asset-groups', { params: { tenantId: selectedTenantId } }).catch(() => ({ data: { data: [] } })),
    ]);
    setTenant(t.data.data); setPending(p.data.data); setGroups(g.data.data);
  }
  useEffect(() => { loadTools(); }, []);
  useEffect(() => { loadTenant(); }, [selectedTenantId]);

  const toolName = (key: string) => tools.find(t => t.key === key)?.name || key;
  function flash(text: string) { setMsg(text); setTimeout(() => setMsg(null), 2500); }

  async function saveTenant() {
    if (!selectedTenantId || !tenant) return;
    await axios.put(`/api/remote-access/tenant/${selectedTenantId}`, tenant);
    flash('Tenant default saved.');
    loadTenant();
  }
  async function applyBulk() {
    if (!selectedTenantId || !bulk.groupId) return;
    const r = await axios.post('/api/remote-access/bulk', { tenantId: selectedTenantId, groupId: Number(bulk.groupId), policy: bulk.policy, tools: bulk.tools });
    flash(`Policy applied to ${r.data.data.updated} asset(s).`);
    loadTenant();
  }
  async function applyPending(policy: 'deny' | 'allow', toolsForAllow: string[]) {
    if (!selectedTenantId || pending.length === 0) return;
    if (!window.confirm(`Apply "${policy}${policy === 'allow' ? ` (${toolsForAllow.map(toolName).join(', ') || 'no tool'})` : ''}" to all ${pending.length} pending asset(s)?`)) return;
    const r = await axios.post('/api/remote-access/bulk', { tenantId: selectedTenantId, assetIds: pending.map(p => p.id), policy, tools: toolsForAllow });
    flash(`Policy applied to ${r.data.data.updated} asset(s).`);
    loadTenant();
  }
  async function saveTool() {
    if (!editing) return;
    await axios.put(`/api/remote-access/tools/${editing.key}`, {
      name: editing.name, processNames: editing.process_names, softwarePatterns: editing.software_patterns,
      publishers: editing.publishers, domains: editing.domains, enabled: editing.enabled,
    });
    setEditing(null); loadTools(); flash('Tool saved.');
  }
  async function toggleTool(t: Tool) {
    await axios.put(`/api/remote-access/tools/${t.key}`, {
      name: t.name, processNames: t.process_names, softwarePatterns: t.software_patterns, publishers: t.publishers, domains: t.domains, enabled: !t.enabled,
    });
    loadTools();
  }
  const listField = (label: string, key: 'process_names' | 'software_patterns' | 'publishers' | 'domains', hint: string) => editing && (
    <label className="block">
      <span className="text-xs text-gray-500">{label} <span className="text-gray-400">— {hint}</span></span>
      <textarea className="input-field w-full mt-1 font-mono text-xs" rows={2} value={editing[key].join('\n')}
        onChange={e => setEditing({ ...editing, [key]: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) })} />
    </label>
  );

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-gray-900 inline-flex items-center gap-2"><ScreenShare size={20} /> Remote Access Tools</h1>
        <p className="text-sm text-gray-500 mt-1">
          TeamViewer, AnyDesk, RustDesk and the like are seen from Windows process events, firewall/proxy/NDR logs and EDR software
          inventories. Each sighting is checked against the asset policy (Asset Center) or, when the asset has none, the tenant default:
          authorized → informational alert; not authorized → critical incident.
        </p>
      </div>
      {msg && <div className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">{msg}</div>}

      {selectedTenantId && tenant && (
        <div className="card p-4 space-y-3">
          <h2 className="font-medium text-sm text-gray-900">Tenant default — {selectedTenantName}</h2>
          <div className="flex flex-wrap items-start gap-4 text-sm">
            <label className="block">
              <span className="text-xs text-gray-500">Assets without an explicit policy</span>
              <select className="input-field mt-1 w-80" value={tenant.policy} onChange={e => setTenant({ ...tenant, policy: e.target.value as TenantPolicy })}>
                <option value="info">Informational only — no incident until a policy is set</option>
                <option value="deny">Deny — any remote access tool is a critical incident</option>
                <option value="allow">Allow only the selected tools</option>
              </select>
            </label>
            {tenant.policy === 'allow' && (
              <div>
                <span className="text-xs text-gray-500">Allowed tools</span>
                <div className="mt-1 grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1">
                  {tools.filter(t => t.enabled).map(t => (
                    <label key={t.key} className="inline-flex items-center gap-1.5 text-xs">
                      <input type="checkbox" checked={tenant.tools.includes(t.key)}
                        onChange={e => setTenant({ ...tenant, tools: e.target.checked ? [...tenant.tools, t.key] : tenant.tools.filter(k => k !== t.key) })} />
                      {t.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {isAdmin && <button onClick={saveTenant} className="btn-primary text-xs self-end inline-flex items-center gap-1"><Save size={13} /> Save</button>}
          </div>
        </div>
      )}

      {selectedTenantId && (
        <div className="card p-4 space-y-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="font-medium text-sm text-gray-900">Assets with remote tools seen and no policy <span className="badge bg-amber-100 text-amber-800 ml-1">{pending.length}</span></h2>
            {pending.length > 0 && isAdmin && (
              <div className="flex items-center gap-2 text-xs">
                <button onClick={() => applyPending('deny', [])} className="btn-secondary text-xs">Deny on all</button>
                <button onClick={() => applyPending('allow', [...new Set(pending.map(p => p.remote_access_last_tool))])} className="btn-secondary text-xs"
                  title="Allow, on each pending asset, the tools observed so far across the pending list">Allow the observed tools on all</button>
              </div>
            )}
          </div>
          {pending.length === 0 ? <p className="text-sm text-gray-400">Nothing to review.</p> : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead><tr><th className="table-header">Asset</th><th className="table-header">Tool</th><th className="table-header">Signal</th><th className="table-header">Last seen</th></tr></thead>
                <tbody>
                  {pending.map(p => (
                    <tr key={p.id} className="border-t border-gray-100">
                      <td className="table-cell"><Link to={`/asset-center/${p.id}`} className="text-primary-700 hover:underline font-medium">{p.hostname}</Link>{p.ip_address && <span className="text-gray-400 ml-2 text-xs">{p.ip_address}</span>}</td>
                      <td className="table-cell">{p.tool_name || p.remote_access_last_tool}</td>
                      <td className="table-cell text-gray-500">{p.remote_access_last_signal}</td>
                      <td className="table-cell text-gray-500">{new Date(p.remote_access_last_seen_at).toLocaleString('en-GB')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {groups.length > 0 && (
            <div className="flex flex-wrap items-end gap-3 text-sm border-t border-gray-100 pt-3">
              <label className="block">
                <span className="text-xs text-gray-500">Apply a policy to a whole asset group</span>
                <select className="input-field mt-1" value={bulk.groupId} onChange={e => setBulk({ ...bulk, groupId: e.target.value })}>
                  <option value="">Select group…</option>
                  {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
                </select>
              </label>
              <select className="input-field" value={bulk.policy} onChange={e => setBulk({ ...bulk, policy: e.target.value as 'deny' | 'allow' })}>
                <option value="allow">Allow only…</option><option value="deny">Deny</option>
              </select>
              {bulk.policy === 'allow' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1">
                  {tools.filter(t => t.enabled).map(t => (
                    <label key={t.key} className="inline-flex items-center gap-1.5 text-xs">
                      <input type="checkbox" checked={bulk.tools.includes(t.key)}
                        onChange={e => setBulk({ ...bulk, tools: e.target.checked ? [...bulk.tools, t.key] : bulk.tools.filter(k => k !== t.key) })} />
                      {t.name}
                    </label>
                  ))}
                </div>
              )}
              <button onClick={applyBulk} disabled={!bulk.groupId} className="btn-primary text-xs disabled:opacity-50">Apply to group</button>
            </div>
          )}
        </div>
      )}
      {!selectedTenantId && (
        <div className="card p-4 text-sm text-gray-500">Select a tenant in the “Viewing” menu to set its default policy and review pending assets.</div>
      )}

      <div className="card overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <h2 className="font-medium text-sm text-gray-900">Catalog <span className="text-gray-400 font-normal">({tools.length} tools, global)</span></h2>
          {isPlatform && (
            <button onClick={() => setEditing({ key: '', name: '', process_names: [], software_patterns: [], publishers: [], domains: [], enabled: true, builtin: false })}
              className="btn-secondary text-xs inline-flex items-center gap-1"><Plus size={13} /> Add tool</button>
          )}
        </div>
        <table className="w-full text-sm">
          <thead><tr><th className="table-header">Tool</th><th className="table-header">Processes</th><th className="table-header">Domains</th><th className="table-header">Enabled</th>{isPlatform && <th className="table-header"></th>}</tr></thead>
          <tbody>
            {tools.map(t => (
              <tr key={t.key} className="border-t border-gray-100">
                <td className="table-cell font-medium text-gray-900">{t.name} <span className="text-gray-400 font-mono text-xs">{t.key}</span></td>
                <td className="table-cell text-xs text-gray-600 font-mono">{t.process_names.join(', ')}</td>
                <td className="table-cell text-xs text-gray-600 font-mono">{t.domains.join(', ')}</td>
                <td className="table-cell">
                  {isPlatform
                    ? <button onClick={() => toggleTool(t)} className={`badge ${t.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{t.enabled ? 'Enabled' : 'Disabled'}</button>
                    : <span className={`badge ${t.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{t.enabled ? 'Enabled' : 'Disabled'}</span>}
                </td>
                {isPlatform && <td className="table-cell text-right"><button onClick={() => setEditing(t)} className="text-gray-400 hover:text-primary-600"><Pencil size={14} /></button></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900">{editing.builtin || tools.some(t => t.key === editing.key) ? `Edit ${editing.name}` : 'New tool'}</h2>
              <button onClick={() => setEditing(null)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <label className="block"><span className="text-xs text-gray-500">Key (letters, digits, _)</span>
                  <input className="input-field w-full mt-1 font-mono" value={editing.key} disabled={tools.some(t => t.key === editing.key)}
                    onChange={e => setEditing({ ...editing, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })} /></label>
                <label className="block"><span className="text-xs text-gray-500">Name</span>
                  <input className="input-field w-full mt-1" value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} /></label>
              </div>
              {listField('Process names', 'process_names', 'one per line, e.g. anydesk.exe (Windows event 4688)')}
              {listField('Software name patterns', 'software_patterns', 'one per line, SQL LIKE syntax, e.g. anydesk%')}
              {listField('Publishers', 'publishers', 'one per line, exact match on the installer publisher')}
              {listField('Domains', 'domains', 'one per line, matched by suffix, e.g. anydesk.com')}
            </div>
            <div className="flex justify-end gap-2 px-5 pb-5">
              <button onClick={() => setEditing(null)} className="btn-secondary">Cancel</button>
              <button onClick={saveTool} disabled={!editing.key || !editing.name.trim()} className="btn-primary disabled:opacity-50">Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
